import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { sendForm } from "../lib/forms";
import { toast } from "sonner";
import { FAQS, IMAGES } from "../data/content";

const CATS = ["All", "Sessions & Confidentiality", "Payment & Insurance", "Tech Setup", "Products & Downloads"];

function Item({ faq, open, onToggle }) {
  return (
    <div
      className={`overflow-hidden rounded-3xl border transition-colors duration-300 ${
        open ? "border-pine/30 bg-white" : "border-pine/10 bg-white/70"
      }`}
      data-testid="faq-item"
    >
      <button
        data-testid="faq-toggle"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 p-6 text-left"
      >
        <span className="font-serif text-lg text-ink sm:text-xl">{faq.q}</span>
        <motion.span
          animate={{ rotate: open ? 45 : 0 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
            open ? "bg-pine text-cream" : "bg-sagelight/70 text-pine"
          }`}
        >
          <Plus className="h-4 w-4" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="px-6 pb-6 text-sm leading-relaxed text-bodytext">{faq.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQs() {
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const [openIdx, setOpenIdx] = useState(0);
  const [ask, setAsk] = useState({ name: "", email: "", message: "" });

  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return FAQS.filter(
      (f) =>
        (cat === "All" || f.cat === cat) &&
        (!needle || f.q.toLowerCase().includes(needle) || f.a.toLowerCase().includes(needle))
    );
  }, [cat, q]);

  const sendAsk = async (e) => {
    e.preventDefault();
    try {
      await sendForm("FAQ question", { ...ask, topic: "FAQ" });
      toast.success("Question received — we'll answer it personally.");
      setAsk({ name: "", email: "", message: "" });
    } catch {
      toast.error("Couldn't send right now — please email connectbackofthemind@gmail.com.");
    }
  };

  return (
    <div>
      <PageHero
        eyebrow="FAQs"
        lines={["Honest answers,", "before you ask."]}
        sub="Confidentiality, payments, tech setup and product policies — the things people wonder but don't always feel like asking out loud."
        image={IMAGES.calmWindow}
        alt="A quiet moment of reflection"
      />

      <section className="mx-auto max-w-4xl px-5 py-16 sm:px-8">
        <Reveal>
          <div className="flex flex-col gap-5">
            <div className="flex flex-wrap gap-2" data-testid="faq-category-tabs">
              {CATS.map((c) => (
                <button
                  key={c}
                  data-testid={`faq-tab-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => setCat(c)}
                  className={`rounded-full border px-4 py-2 text-xs font-semibold transition-all duration-300 ${
                    cat === c
                      ? "border-pine bg-pine text-cream"
                      : "border-pine/20 bg-white text-bodytext hover:border-pine/50"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <input
              data-testid="faq-search-input"
              className="input-soft"
              placeholder="Search the FAQs…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Search FAQs"
            />
          </div>
        </Reveal>

        <div className="mt-10 space-y-4" data-testid="faq-list">
          {list.length === 0 && (
            <p data-testid="faq-empty-state" className="py-16 text-center font-serif text-2xl italic text-ink">
              No answer under that rock yet — ask us below.
            </p>
          )}
          {list.map((f, i) => (
            <Reveal key={f.q} delay={Math.min(i * 0.04, 0.3)} y={16}>
              <Item faq={f} open={openIdx === f.q} onToggle={() => setOpenIdx(openIdx === f.q ? null : f.q)} />
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1} className="mt-14">
          <form
            onSubmit={sendAsk}
            data-testid="faq-ask-form"
            className="rounded-[2rem] bg-pinedeep p-8 text-cream sm:p-10"
          >
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-blush">
              Still wondering?
            </p>
            <h2 className="mt-3 font-serif text-3xl">Ask us directly.</h2>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <input
                data-testid="faq-ask-name-input"
                required
                className="input-soft border-cream/20 bg-white/5 text-cream placeholder:text-cream/35"
                placeholder="Your name"
                value={ask.name}
                onChange={(e) => setAsk({ ...ask, name: e.target.value })}
              />
              <input
                data-testid="faq-ask-email-input"
                required
                type="email"
                className="input-soft border-cream/20 bg-white/5 text-cream placeholder:text-cream/35"
                placeholder="Email"
                value={ask.email}
                onChange={(e) => setAsk({ ...ask, email: e.target.value })}
              />
              <textarea
                data-testid="faq-ask-message-input"
                required
                className="input-soft min-h-24 border-cream/20 bg-white/5 text-cream placeholder:text-cream/35 sm:col-span-2"
                placeholder="Your question…"
                value={ask.message}
                onChange={(e) => setAsk({ ...ask, message: e.target.value })}
              />
              <button
                data-testid="faq-ask-submit-button"
                className="rounded-full bg-cream py-3.5 text-sm font-semibold text-pinedeep transition-all hover:-translate-y-0.5 hover:bg-blush sm:col-span-2"
              >
                Send question
              </button>
            </div>
          </form>
        </Reveal>
      </section>
    </div>
  );
}
